import { Link } from 'react-router-dom';
import UtilityBar from '../components/layout/UtilityBar';
import Nav from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import ImageSlot from '../components/ui/ImageSlot';
import { CASE_STUDIES } from '../data/caseStudies';

/**
 * Case Study detail — deep proof story (Malta is the reference deployment).
 * The same component handles all case-study slugs via the route param.
 */
export default function CaseStudyDetail() {
  return (
    <div style={{ background: '#fff' }}>
      <UtilityBar />
      <Nav variant="light" />

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: 'var(--bg-cream)', padding: '48px 0 0' }}>
        <div className="lc-container">
          <div className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 32 }}>
            <Link to="/" style={{ color: 'var(--muted)' }}>Home</Link>
            <span>›</span>
            <a href="#" style={{ color: 'var(--muted)' }}>Case Studies</a>
            <span>›</span>
            <span className="font-semibold" style={{ color: 'var(--ink)' }}>Malta · Public Schools</span>
          </div>

          <div className="grid items-center pb-14" style={{ gridTemplateColumns: '1.3fr 1fr', gap: 60 }}>
            <div>
              <span className="font-bold tracking-wider" style={{ fontSize: 11, padding: '5px 10px', borderRadius: 999, background: 'rgba(43,108,217,0.12)', color: 'var(--blue)' }}>🇲🇹 MALTA · PUBLIC SCHOOLS</span>
              <h1 className="mt-4" style={{ fontSize: 64, lineHeight: 0.98, letterSpacing: '-0.03em' }}>
                How <em className="italic" style={{ color: 'var(--blue)' }}>every</em> Maltese high school <br />got an AI literacy course.
              </h1>
              <p className="mt-5 leading-relaxed" style={{ fontSize: 18, color: 'var(--ink-2)', maxWidth: 560 }}>
                An AI literacy curriculum built with Malta&apos;s Ministry of Education — now running in every secondary school, in Maltese and English.
              </p>
              <div className="flex gap-3 mt-7">
                <a href="#" className="lc-btn primary">Read the full report ↓</a>
              </div>
              <div className="mt-5" style={{ fontSize: 13, color: 'var(--muted)' }}>
                March 2026 · 12 min read
              </div>
            </div>

            <div className="relative">
              <ImageSlot id="cs-malta-hero" style={{ width: '100%', height: 460, borderRadius: 20 }} shape="rounded" radius="20"
                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1200&auto=format&fit=crop&q=80"
                placeholder="Drop a hero photo of Maltese students" />
              <div className="absolute"
                style={{ bottom: -24, left: -24, background: '#fff', padding: '16px 22px', borderRadius: 14, boxShadow: 'var(--shadow-lg)', border: '1px solid var(--line)' }}>
                <div className="font-bold uppercase tracking-widest" style={{ fontSize: 11, color: 'var(--muted)' }}>Reach</div>
                <div className="font-serif font-bold mt-0.5" style={{ fontSize: 32, color: 'var(--ink)' }}>47<span style={{ color: 'var(--blue)' }}> schools</span></div>
                <div className="mt-0.5" style={{ fontSize: 12, color: 'var(--muted)' }}>100% of secondary schools in Malta</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AT A GLANCE */}
      <section className="on-navy" style={{ background: 'var(--navy)', padding: '64px 0' }}>
        <div className="lc-container">
          <div className="grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, background: 'rgba(255,255,255,0.06)', padding: 4, borderRadius: 20 }}>
            {[
              { label: 'Country',   value: '🇲🇹 Malta' },
              { label: 'Audience',  value: 'Secondary students, ages 11–18' },
              { label: 'Languages', value: 'Maltese · English' },
              { label: 'Duration',  value: '2 years (ongoing)' },
              { label: 'Funding',   value: 'EU + Min. of Edu.' },
            ].map((f) => (
              <div key={f.label} style={{ padding: '20px 22px', background: 'var(--navy)', borderRadius: 16 }}>
                <div className="font-bold uppercase tracking-widest mb-2" style={{ fontSize: 11, color: 'var(--yellow-2)' }}>{f.label}</div>
                <div className="font-semibold text-white leading-snug" style={{ fontSize: 16 }}>{f.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHALLENGE / SOLUTION / RESULTS */}
      <section className="lc-section">
        <div className="lc-container" style={{ maxWidth: 1080 }}>
          <div className="grid items-start" style={{ gridTemplateColumns: '260px 1fr', gap: 56 }}>
            <aside>
              <div className="sticky" style={{ top: 100 }}>
                <div className="font-bold uppercase tracking-widest mb-4" style={{ fontSize: 11, color: 'var(--muted)' }}>On this page</div>
                <ul className="list-none p-0 m-0 flex flex-col gap-1" style={{ borderLeft: '2px solid var(--line)' }}>
                  {['The Challenge', 'The Solution', 'The Rollout', 'Results', 'What Teachers Say', 'Press Coverage', "What's Next"].map((s, i) => (
                    <li key={s}>
                      <a href="#" className="block" style={{
                        padding: '6px 14px',
                        marginLeft: -2,
                        borderLeft: i === 1 ? '2px solid var(--yellow)' : '2px solid transparent',
                        fontSize: 13,
                        fontWeight: i === 1 ? 700 : 500,
                        color: i === 1 ? 'var(--ink)' : 'var(--muted)',
                      }}>{s}</a>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--line)' }}>
                  <div className="font-bold uppercase tracking-widest mb-3" style={{ fontSize: 11, color: 'var(--muted)' }}>Share</div>
                  <div className="flex gap-2">
                    {['𝕏', 'in', 'f', '✉'].map((s) => (
                      <button key={s} type="button" className="rounded-full font-bold"
                        style={{ width: 36, height: 36, background: 'var(--bg-soft)', color: 'var(--ink)' }}>{s}</button>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            <div className="text-ink-2" style={{ fontSize: 17, lineHeight: 1.65 }}>
              <h2 style={{ fontSize: 36 }}>The Challenge</h2>
              <p className="mt-4">
                Malta is small — 500,000 people on an island in the Mediterranean — but ambitious. In 2023 the Ministry of Education announced that every secondary school student in the country would graduate AI-literate by 2027. The problem? <strong>There aren&apos;t enough AI teachers in Malta to teach a single class, let alone every school in the country.</strong>
              </p>
              <p className="mt-4">
                Traditional curriculum vendors quoted 18–24 months to develop materials in Maltese and English, with hundreds of in-person training hours per teacher. By the time the program would be live, the AI landscape would have shifted twice.
              </p>

              <Pullquote
                quote="We needed a partner who could move at the speed of AI, not the speed of textbooks."
                author="Hon. Clifton Grima"
                role="Minister of Education, Republic of Malta"
              />

              <h2 style={{ fontSize: 36, marginTop: 56 }}>The Solution</h2>
              <p className="mt-4">
                Lincoln stepped in with a fundamentally different approach: <strong>don&apos;t train teachers to teach AI — give every student a voice-AI tutor</strong>. We co-designed a 24-lesson AI literacy course with the Ministry, then deployed Lincoln as the primary instructor. Teachers became coaches and facilitators, not lecturers.
              </p>

              <h2 style={{ fontSize: 36, marginTop: 56 }}>The Rollout</h2>
              <Timeline />

              <h2 style={{ fontSize: 36, marginTop: 56 }}>Results</h2>
              <ResultsGrid />

              <h2 style={{ fontSize: 36, marginTop: 56 }}>What Teachers Say</h2>
              <div className="grid mt-6" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <TeacherQuote
                  name="Maria Cassar"
                  role="Head of Science, St. Aloysius College"
                  quote="My students argue with Lincoln in Maltese about whether AI can be biased. That's an essay we used to have to assign — now they choose to have it."
                />
                <TeacherQuote
                  name="John Borg"
                  role="ICT Coordinator, Maria Regina College"
                  quote="The certificate matters to parents. Suddenly AI literacy is something you graduate with — not something you opt into."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="lc-section" style={{ background: 'var(--bg-cream)' }}>
        <div className="lc-container">
          <div className="flex justify-between items-end mb-9">
            <div>
              <span className="lc-eyebrow">More case studies</span>
              <h2 className="mt-3.5">What we&apos;re doing in other places.</h2>
            </div>
            <a href="#" className="lc-link" style={{ color: 'var(--ink-2)' }}>All case studies →</a>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {CASE_STUDIES.slice(2, 5).map((cs) => (
              <article key={cs.slug} className="lc-card overflow-hidden">
                <div className="relative" style={{ height: 160, background: `linear-gradient(135deg, ${cs.color}, ${cs.color}cc)` }}>
                  <ImageSlot id={`cs-related-${cs.img}`} style={{ position: 'absolute', inset: 0 }} shape="rect" src={cs.photo} placeholder={`Photo: ${cs.region}`} />
                  <div className="absolute font-bold pointer-events-none"
                    style={{ top: 12, left: 12, background: 'rgba(255,255,255,0.95)', color: cs.color, padding: '4px 10px', borderRadius: 999, fontSize: 11 }}>{cs.tag}</div>
                </div>
                <div style={{ padding: 22 }}>
                  <div className="font-semibold uppercase tracking-wider" style={{ fontSize: 11, color: 'var(--muted)' }}>{cs.region}</div>
                  <h3 className="mt-2" style={{ fontSize: 20 }}>{cs.title}</h3>
                  <p className="mt-2.5" style={{ color: 'var(--muted)', fontSize: 14 }}>{cs.desc}</p>
                  <a href="#" className="lc-link mt-3.5">Read story →</a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="on-navy" style={{ background: 'var(--navy)', padding: '100px 0' }}>
        <div className="lc-container">
          <div className="grid items-center" style={{ gridTemplateColumns: '1fr auto', gap: 48 }}>
            <div>
              <span className="lc-eyebrow on-dark">Ready to talk?</span>
              <h2 className="text-white mt-3.5" style={{ fontSize: 56, maxWidth: 700 }}>
                Want a Lincoln program for your country, district, or school?
              </h2>
              <p className="mt-4" style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)', maxWidth: 600 }}>
                We&apos;ve done it in 10 countries. Your context is unique — let&apos;s design what works for you.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a href="#" className="lc-btn ghost-light lg">Download case study PDF</a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Pullquote({ quote, author, role }) {
  return (
    <blockquote className="rounded-r-2xl"
      style={{ margin: '40px 0', padding: '32px 36px', background: 'linear-gradient(135deg, rgba(43,108,217,0.06), rgba(240,184,12,0.04))', borderLeft: '4px solid var(--yellow)' }}>
      <div className="font-serif font-medium leading-tight" style={{ fontSize: 28, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
        &ldquo;{quote}&rdquo;
      </div>
      <div className="mt-4 flex items-center gap-3">
        <ImageSlot id={`q-${author.replace(/\s/g, '-')}`} style={{ width: 44, height: 44 }} shape="circle" placeholder="" />
        <div>
          <div className="font-bold" style={{ fontSize: 14, color: 'var(--ink)' }}>{author}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{role}</div>
        </div>
      </div>
    </blockquote>
  );
}

function Timeline() {
  const items = [
    { date: 'Jun 2024', title: 'MOU signed with Ministry',  desc: 'Lincoln + MOE agree on scope, curriculum, and timeline.' },
    { date: 'Sep 2024', title: 'Co-designed curriculum',     desc: '24 lessons in Maltese and English, reviewed by 12 teachers.' },
    { date: 'Jan 2025', title: 'Pilot in 6 schools',          desc: 'Lincoln deployed to 1,400 students. Iterated based on real classroom feedback.' },
    { date: 'Sep 2025', title: 'National rollout',            desc: 'All 47 secondary schools in Malta — public, church, and independent.' },
    { date: 'Mar 2026', title: 'First cohort certified',      desc: '8,200 students earned the MOE-branded AI Literacy certificate.' },
  ];
  return (
    <div className="relative pl-8 mt-6">
      <div className="absolute" style={{ left: 8, top: 8, bottom: 8, width: 2, background: 'var(--line)' }} />
      {items.map((it, i) => (
        <div key={it.date} className="relative" style={{ paddingBottom: i === items.length - 1 ? 0 : 24 }}>
          <div className="absolute rounded-full bg-white"
            style={{ left: -28, top: 6, width: 18, height: 18, border: '3px solid var(--yellow)', boxShadow: '0 0 0 4px var(--bg-cream)' }} />
          <div className="font-bold tracking-wider" style={{ fontSize: 12, color: 'var(--blue)' }}>{it.date.toUpperCase()}</div>
          <div className="font-serif font-semibold mt-1" style={{ fontSize: 18, color: 'var(--ink)' }}>{it.title}</div>
          <div className="mt-1.5" style={{ fontSize: 15, color: 'var(--muted)' }}>{it.desc}</div>
        </div>
      ))}
    </div>
  );
}

function ResultsGrid() {
  const results = [
    { value: '47',     unit: 'schools',    label: '100% of Maltese secondary schools enrolled' },
    { value: '12,481', unit: 'students',   label: 'Active learners — every secondary student' },
    { value: '8,200',  unit: 'certs issued', label: 'MOE-branded AI Literacy certificates earned' },
    { value: '+47',    unit: 'pts',        label: 'Average lift in AI literacy assessment scores' },
    { value: '94%',    unit: '',           label: 'Of teachers say Lincoln saves them prep time' },
    { value: '98%',    unit: '',           label: 'Course completion rate (vs. 31% online avg.)' },
  ];
  return (
    <div className="grid mt-6" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
      {results.map((r) => (
        <div key={r.label} style={{ padding: 24, background: 'var(--bg-cream)', borderRadius: 16, border: '1px solid var(--line)' }}>
          <div className="flex items-baseline gap-1.5">
            <div className="font-serif font-semibold leading-none" style={{ fontSize: 52, color: 'var(--ink)', letterSpacing: '-0.02em' }}>{r.value}</div>
            <div className="font-bold" style={{ fontSize: 14, color: 'var(--blue)' }}>{r.unit}</div>
          </div>
          <div className="mt-2.5 leading-relaxed" style={{ fontSize: 13, color: 'var(--muted)' }}>{r.label}</div>
        </div>
      ))}
    </div>
  );
}

function TeacherQuote({ name, role, quote }) {
  return (
    <div style={{ background: 'var(--bg-cream)', padding: 28, borderRadius: 16, border: '1px solid var(--line)' }}>
      <svg width="28" height="22" viewBox="0 0 28 22" fill="var(--yellow)" style={{ marginBottom: 12 }}>
        <path d="M0 22 V 11 a 11 11 0 0 1 11 -11 V 6 a 6 6 0 0 0 -6 6 H 11 V 22 Z M 17 22 V 11 a 11 11 0 0 1 11 -11 V 6 a 6 6 0 0 0 -6 6 H 28 V 22 Z" />
      </svg>
      <p className="italic leading-relaxed" style={{ fontSize: 16, color: 'var(--ink)' }}>&ldquo;{quote}&rdquo;</p>
      <div className="mt-4 flex items-center gap-3">
        <ImageSlot id={`teach-${name.replace(/\s/g, '-')}`} style={{ width: 40, height: 40 }} shape="circle" placeholder="" />
        <div>
          <div className="font-bold" style={{ fontSize: 14 }}>{name}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{role}</div>
        </div>
      </div>
    </div>
  );
}
